// Work capsules: time formatting and totals -- pure, tested by tiempo.test.mjs.
// Capsule times come from the server as naive Chile local ISO strings
// ('2026-09-14T10:00:00'), shown as they are: no time zone conversion.

// 5400 -> '1 h 30 min'; 2700 -> '45 min'; 30 -> '< 1 min'
export function duracion(seg) {
  if (seg == null) return '—'
  if (seg < 60) return seg > 0 ? '< 1 min' : '0 min'
  const h = Math.floor(seg / 3600), m = Math.floor((seg % 3600) / 60)
  return h ? (m ? `${h} h ${m} min` : `${h} h`) : `${m} min`
}

// 5400 -> '1,5' (hours, for tables and charts)
export const horas = (seg, dec = 1) => (seg / 3600).toFixed(dec).replace('.', ',')

export const hora = (iso) => (iso ? iso.slice(11, 16) : '')      // 'HH:MM'
export const dia = (iso) => (iso ? iso.slice(0, 10) : '')        // 'YYYY-MM-DD'

// Totals of a list of capsules: overall, and per person and per parte
// (each sorted by total, highest first).
export function resumir(caps) {
  const total = { seg: 0, normal: 0, extra: 0, n: caps.length }
  const personas = new Map(), partes = new Map()
  for (const c of caps) {
    const k = c.hora_extra ? 'extra' : 'normal'
    total.seg += c.segundos
    total[k] += c.segundos
    if (!personas.has(c.persona_id)) {
      personas.set(c.persona_id, { persona_id: c.persona_id, nombre: c.persona_nombre, seg: 0, normal: 0, extra: 0, n: 0, partes: new Set() })
    }
    const p = personas.get(c.persona_id)
    p.seg += c.segundos; p[k] += c.segundos; p.n++; p.partes.add(c.parte_id)
    if (!partes.has(c.parte_id)) {
      partes.set(c.parte_id, { parte_id: c.parte_id, numero: c.parte_numero, nombre: c.parte_nombre,
        unidad: c.unidad_codigo, seg: 0, normal: 0, extra: 0, n: 0, personas: new Set() })
    }
    const q = partes.get(c.parte_id)
    q.seg += c.segundos; q[k] += c.segundos; q.n++; q.personas.add(c.persona_nombre)
  }
  const orden = (a, b) => b.seg - a.seg
  return {
    total,
    porPersona: [...personas.values()].map(p => ({ ...p, partes: p.partes.size })).sort(orden),
    porParte: [...partes.values()].map(p => ({ ...p, personas: [...p.personas].sort() })).sort(orden),
  }
}

// Capsules grouped by the day they started, newest day first, each day's
// capsules in time order: [{ dia, caps, seg }].
export function porDia(caps) {
  const m = new Map()
  for (const c of caps) {
    const d = dia(c.inicio)
    if (!m.has(d)) m.set(d, [])
    m.get(d).push(c)
  }
  return [...m.entries()]
    .sort(([a], [b]) => (a < b ? 1 : -1))
    .map(([d, l]) => {
      const caps = [...l].sort((a, b) => (a.inicio < b.inicio ? -1 : 1))
      return { dia: d, caps, seg: caps.reduce((s, c) => s + c.segundos, 0) }
    })
}

// The quick ranges of the reports, as { desde, hasta } ISO dates.
export function rangos(hoy) {
  const d = new Date(`${hoy}T12:00:00`)
  const iso = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`
  const lunes = new Date(d); lunes.setDate(d.getDate() - ((d.getDay() + 6) % 7))
  const primero = new Date(d.getFullYear(), d.getMonth(), 1, 12)
  const mesPasado = new Date(d.getFullYear(), d.getMonth() - 1, 1, 12)
  const finMesPasado = new Date(d.getFullYear(), d.getMonth(), 0, 12)
  return {
    hoy: { desde: hoy, hasta: hoy },
    semana: { desde: iso(lunes), hasta: hoy },
    mes: { desde: iso(primero), hasta: hoy },
    mesAnterior: { desde: iso(mesPasado), hasta: iso(finMesPasado) },
  }
}

// A running capsule's seconds right now, from the seconds the server
// reported when the data arrived (`recibido`: Date.now() then) -- so the
// browser's clock and time zone don't matter -- never past its automatic
// stop. One waiting out lunch (empieza_en > 0) counts only once it starts.
export function segundosVivos(c, recibido, ahoraMs = Date.now()) {
  if (!c.abierta) return c.segundos
  const tope = c.limite ? Math.floor((Date.parse(c.limite) - Date.parse(c.inicio)) / 1000) : Infinity
  const pasados = Math.max(0, Math.floor((ahoraMs - recibido) / 1000) - (c.empieza_en || 0))
  return Math.min(tope, c.segundos + pasados)
}

// A running capsule still waiting for lunch to end?
export const enColacion = (c, recibido, ahoraMs = Date.now()) =>
  !!c?.abierta && (c.empieza_en || 0) * 1000 > ahoraMs - recibido
